import { useMemo } from 'react'
import { MdAdd } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'

import type { ReactElement, ChangeEvent } from 'react'
import type { IAppSummary } from '~/types/application'
import type { IPageContainer } from '~/types/page-container'

import { getFile } from '~/api/file'
import Button from '~/components/Buttons/Button'
import ContentLayout from '~/components/ContentLayout'
import Filters, { FiltersContent } from '~/components/Filters'
import SearchInput from '~/components/Inputs/Basic/SearchInput'
import SelectInput from '~/components/Inputs/Basic/SelectInput'
import TextInput from '~/components/Inputs/Basic/TextInput'
import Layout from '~/components/Layouts/Layout'
import PageContainerCard from '~/components/PageContainerCard'
import Pagination from '~/components/Pagination'
import Section from '~/components/Section'
import CustomAppOption, { formatAppOptionLabel } from '~/components/SelectOptions/CustomAppOption'
import ShowItems from '~/components/ShowItems'
import ViewTitle from '~/components/ViewTitle'
import { LOCAL_STORAGE, dataNotAvailableText } from '~/const'
import { orderByOptions, platformOptions, statusOptions } from '~/const/page-containers'
import useDocumentTitle from '~/hooks/useDocumentTitle'
import { DEFAULT_SEARCH, useTablePagination } from '~/hooks/useTablePagination'
import { useTouchDown } from '~/hooks/useTouchDown'
import { useAppsListQuery } from '~/queries/applications'
import { usePageContainersListQuery } from '~/queries/page-containers'
import { PAGE_CONTAINER_STATUS } from '~/types/page-container'
import { downloadFromUrl } from '~/utils/file'
import { debounce } from '~/utils/function'
import { getTemplateFromKey } from '~/utils/template'

import './styles.scss'

const ENTRIES_NAME = 'page-containers'

const PageContainersListing = (): ReactElement => {
  /** Local state */

  const navigate = useNavigate()

  const paginationApi = useTablePagination(ENTRIES_NAME)

  const { isTouchDown, setIsTouchDown } = useTouchDown('Meta')

  const { filters, orderBy, page, search, setFilters, setOrderBy, setPage, setSearch, setSize, size } = paginationApi

  const upToDateFilters = useMemo(
    () =>
      JSON.parse(localStorage.getItem(LOCAL_STORAGE.PAGE_CONTAINER_FILTERS) || '{}') ?? {
        ...filters,
      },
    [filters]
  )

  const { appId, ...rest } = upToDateFilters

  /** Queries & Mutations */

  const { data: apps } = useAppsListQuery({})

  const MwMApp = apps?.data?.filter((element) => element.owned)

  const filterAppByPlatform = (selectedPlatform: string) => {
    return selectedPlatform
      ? MwMApp?.filter((app) => app.device_type === selectedPlatform)
      : apps?.data?.filter((element) => element.owned)
  }

  const MwMAppOptions: (IOption & { appKey?: string; device?: string; url?: string })[] =
    filterAppByPlatform(upToDateFilters['device-type'] as string)?.map(
      ({ app_icon_url, app_key, device_type, name }) => ({
        appKey: app_key,
        device: device_type,
        label: name,
        url: app_icon_url,
        value: name,
      })
    ) || []

  MwMAppOptions.unshift({ label: 'All', value: '' })

  const pageContainerCardMenu = (pageContainer: IPageContainer) => {
    const items = []

    if (pageContainer.status !== PAGE_CONTAINER_STATUS.DRAFT) {
      items.push({
        label: 'View',
        onClick: () => navigate(`/dynamic-screens/page-containers/${pageContainer.uuid}`),
      })
    }

    if (pageContainer.template_key) {
      if (getTemplateFromKey(pageContainer.template_key)) {
        items.push({
          label: 'Create from',
          onClick: () =>
            navigate('/dynamic-screens/page-containers/duplication', {
              state: { pageContainer },
            }),
        })
      }

      if (pageContainer.status === PAGE_CONTAINER_STATUS.DRAFT) {
        items.push({
          label: 'View',
          onClick: () => navigate(`/dynamic-screens/page-containers/${pageContainer.uuid}`),
        })
      }
    }

    const workspaceFileId = pageContainer.platform_dependant_properties?.workspace_file_id

    if (!!workspaceFileId) {
      items.push({
        label: 'Download',
        onClick: async () => {
          const { download_url } = await getFile({ fileId: workspaceFileId })
          downloadFromUrl(download_url)
        },
      })
    }

    return [{ items }]
  }

  const { data: pageContainers, isLoading: isPageContainersLoading } = usePageContainersListQuery({
    'order-by': orderBy,
    page,
    size,
    ...rest,
    ...search,
  })

  const totalPageDisplayed = () => {
    if (pageContainers?.total) {
      return Math.ceil(pageContainers.total / size) < 1 ? 1 : Math.ceil(pageContainers.total / size)
    }
    return 0
  }

  const setLocalStoragePageContainersFilters = (obj: Record<string, string | string[] | null>): void => {
    const filters = JSON.parse(localStorage.getItem(LOCAL_STORAGE.PAGE_CONTAINER_FILTERS) || '{}')
    const newFilters = { ...filters, ...obj }
    localStorage.setItem(LOCAL_STORAGE.PAGE_CONTAINER_FILTERS, JSON.stringify(newFilters))
  }

  /** Effects */

  useDocumentTitle('DS Page containers')

  /** Handlers */

  const handleSelectFilterChange = (
    key: string,
    option: IOption | (IOption & { appKey?: string; device?: string; url?: string })
  ) => {
    const filtersOverride = {
      appId: 'appKey' in option ? option.value : '',
      [key]: 'appKey' in option ? option.appKey ?? '' : option.value,
    }

    if (key === 'device-type' && !option.value) {
      filtersOverride['app-key'] = ''
    }

    setFilters({
      ...filters,
      ...filtersOverride,
    })
    setLocalStoragePageContainersFilters(filtersOverride)
  }

  const handleTextInputFilterChange = (key: string, evt: ChangeEvent<HTMLInputElement>) => {
    setFilters({ ...filters, [key]: evt.target.value })
    setLocalStoragePageContainersFilters({ [key]: evt.target.value })
  }

  const handleOrderByChange = (option: IOption) => setOrderBy(option.value)

  const handleSearchTextChange = debounce((event: ChangeEvent<HTMLInputElement>) => {
    const text = event.target.value
    setSearch(text ? { title: text } : DEFAULT_SEARCH)
    setPage(0)
  }, 500)

  const handleItemPerPageChange = (value: number) => {
    setPage(0)
    setSize(value)
  }

  const handleNewPageContainerClick = () => navigate('/dynamic-screens/page-containers/creation')

  const handlePageContainerCardClick = (pageContainerId: string) => {
    if (isTouchDown) {
      window.open(`/dynamic-screens/page-containers/${pageContainerId}`, '_blank', 'rel=noopener noreferrer')
      setIsTouchDown(false)
    } else navigate(pageContainerId)
  }

  const handleGoToPage = (page: number) => setPage(page)

  const handleGoBack = () => setPage((currentPage) => currentPage - 1)

  const handleGoNext = () => setPage((currentPage) => currentPage + 1)

  return (
    <>
      <ViewTitle className="layout-view-title top-zero">Page containers</ViewTitle>
      <Filters className="page-containers-filters">
        <FiltersContent>
          <div className="filters-row">
            <SelectInput
              className="filter app-filter-input"
              components={{ Option: CustomAppOption }}
              // @ts-expect-error Type 'ReactElement<any, string | JSXElementConstructor<any>>' is not assignable to type 'string'
              getOptionLabel={(option: unknown) => formatAppOptionLabel(option as IOption)}
              isSearchable
              label="Application"
              onChange={(option) => handleSelectFilterChange('app-key', option as IOption)}
              options={MwMAppOptions}
              value={
                upToDateFilters['app-key'] ? MwMAppOptions?.find((option) => option.value === appId) : MwMAppOptions[0]
              }
            />
            <SelectInput
              className="filter"
              isSearchable
              label="Platform"
              onChange={(option) => handleSelectFilterChange('device-type', option as IOption)}
              options={platformOptions}
              value={
                upToDateFilters['device-type']
                  ? platformOptions.find((option) => option.value === upToDateFilters['device-type'])
                  : platformOptions[0]
              }
            />
            <SelectInput
              className="filter"
              isSearchable
              label="Status"
              onChange={(option) => handleSelectFilterChange('status', option as IOption)}
              options={statusOptions}
              value={
                upToDateFilters['status']
                  ? statusOptions.find((option) => option.value === upToDateFilters['status'])
                  : statusOptions[0]
              }
            />
            <TextInput
              className="filter targets-filter-input "
              label="Capabilities"
              onChange={(event) => handleTextInputFilterChange('capabilities', event)}
              placeholder="Capabilities"
              value={upToDateFilters['capabilities'] ?? ''}
            />
            <TextInput
              className="filter targets-filter-input"
              label="Navigation targets"
              onChange={(event) => handleTextInputFilterChange('navigation-target-outputs', event)}
              placeholder="Navitation targets"
              value={upToDateFilters['navigation-target-outputs'] ?? ''}
            />
          </div>
        </FiltersContent>
      </Filters>
      <Layout className="page-container-view" withPadding>
        <ContentLayout loading={isPageContainersLoading}>
          <Section className="section">
            <div className="search-container">
              <div className="left-column">
                <SearchInput className="search-bar" onChange={handleSearchTextChange} />
                <Button onClick={handleNewPageContainerClick}>
                  <MdAdd color="#fff" size={16} />
                  New page container
                </Button>
              </div>
              <div className="right-column">
                <SelectInput
                  className="page-container-sort-by"
                  isSearchable
                  label="Sort by"
                  labelPosition="left"
                  onChange={(data) => handleOrderByChange(data as IOption)}
                  options={orderByOptions}
                  size="small"
                  value={orderByOptions?.find((option) => option.value === orderBy)}
                />
                <ShowItems
                  className="page-container-show-item"
                  entriesName="item"
                  numberPerPage={size}
                  setNumberPerPage={handleItemPerPageChange}
                  totalNumberElements={pageContainers?.total || 0}
                />
              </div>
            </div>
            {pageContainers?.data && pageContainers.data.length > 0 ? (
              <div className="page-containers-card-container">
                {pageContainers.data.map((pageContainer, index) => {
                  const appLogoInfo = apps?.data?.find(
                    (app: IAppSummary) =>
                      app.app_key === pageContainer.app_key && app.device_type === pageContainer.device_type
                  )
                  return (
                    <div className="page-containers-wrapper" key={`page-containers-${index}`}>
                      <PageContainerCard
                        appLogoInfo={appLogoInfo}
                        menu={pageContainerCardMenu(pageContainer)}
                        onClick={() => handlePageContainerCardClick(pageContainer.uuid)}
                        pageContainer={pageContainer}
                        showSubInfos
                      />
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="no-data">{dataNotAvailableText}</div>
            )}
            <div className="pagination-container">
              <Pagination
                currentPage={page}
                goBack={handleGoBack}
                goNext={handleGoNext}
                goToPage={handleGoToPage}
                pageTotal={totalPageDisplayed()}
              />
            </div>
          </Section>
        </ContentLayout>
      </Layout>
    </>
  )
}

export default PageContainersListing
